import axios from "axios";
import JSZip from "jszip";
import { AI_MODELS } from "./aiModels";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  listAll,
} from "firebase/storage";
import { ref, get, set } from "firebase/database";
import { db } from "../firebase-config";
import { generateCXRMulticlassReport } from "./pdfReportGenerator";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function toUrlSearchParams(obj) {
  const params = new URLSearchParams();
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined && obj[key] !== null) {
      params.append(key, String(obj[key]));
    }
  });
  return params;
}

// Helper function to convert base64 to blob
function base64ToBlob(base64, mimeType) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

// Helper function to check if a file is an image file we want to process
function isValidImageFile(filename) {
  const validExtensions = [
    ".dcm",
    ".dicom",
    ".dic",
    ".ima",
    ".img",
    ".png",
    ".jpg",
    ".jpeg",
  ];
  const lowerFilename = filename.toLowerCase();
  return validExtensions.some((ext) => lowerFilename.endsWith(ext));
}

// Helper function to check if a DICOM file should be excluded from ZIP creation
// Excludes dicom-seg-[1-9].dcm and dicom-sr-[1-9].dcm files
function shouldExcludeFromZip(filename) {
  const lowerFilename = filename.toLowerCase();
  
  // Check for dicom-seg-[1-9].dcm pattern
  const dicomSegPattern = /^dicom-seg-[1-9]\.dcm$/;
  if (dicomSegPattern.test(lowerFilename)) {
    return true;
  }
  
  // Check for dicom-sr-[1-9].dcm pattern
  const dicomSrPattern = /^dicom-sr-[1-9]\.dcm$/;
  if (dicomSrPattern.test(lowerFilename)) {
    return true;
  }
  
  return false;
}

// Helper function to generate ZIP filename for backend API calls
// Uses patient_id or task_id with random suffix for backend, base name for Firebase storage
function generateZipFilename(requestBody, forBackend = true) {
  let identifier = requestBody.patient_id || requestBody.task_id || 'unknown';
  
  if (forBackend) {
    // For backend API calls: use full identifier with random suffix
    return `${identifier}.zip`;
  } else {
    // For Firebase storage: remove random suffix (last 4 digits)
    const baseIdentifier = identifier.replace(/\d{4}$/, '');
    return `${baseIdentifier}.zip`;
  }
}

// Helper function to get the folder_ path from the full path
function getFolderBasePath(fullPath) {
  if (!fullPath) return null;

  const parts = fullPath.split("/");
  const folderIndex = parts.findIndex((part) =>
    part.toLowerCase().startsWith("folder_")
  );
  if (folderIndex === -1) return null;

  return parts.slice(0, folderIndex + 1).join("/");
}

// Helper function to recursively list all files in a folder
async function listAllFiles(ref) {
  const allItems = [];

  try {
    // Get all items at this level
    const { items, prefixes } = await listAll(ref);
    allItems.push(...items);

    // Recursively get items from all sub-folders
    for (const prefix of prefixes) {
      const subItems = await listAllFiles(prefix);
      allItems.push(...subItems);
    }
  } catch (error) {
    console.error("Error listing files:", error);
  }

  return allItems;
}

// Helper function to get image file from folder path
async function getDicomFileFromPath(fullPath) {
  try {
    // Get the base folder_ path
    const folderPath = getFolderBasePath(fullPath);
    if (!folderPath) {
      console.error("Could not find folder_ in path:", fullPath);
      throw new Error("Invalid folder path structure");
    }

    

    // Get reference to the storage
    const storage = getStorage();
    const folderRef = storageRef(storage, folderPath);

    // List all files recursively starting from folder_
    const items = await listAllFiles(folderRef);


    // Find the first valid image file
    const imageFile = items.find((item) => isValidImageFile(item.name));
    if (!imageFile) {
      console.error(
        "No valid image files found in folder. Files found:",
        items.map((item) => item.name)
      );
      throw new Error("No valid image file found in the folder");
    }

    

    // Get the download URL
    const downloadURL = await getDownloadURL(imageFile);
   

    // Now fetch the file using the download URL
    const response = await axios.get(downloadURL, {
      responseType: "blob",
    });

    
    return response.data;
  } catch (error) {
    console.error("Error getting image file:", error);
    if (error.code === "storage/object-not-found") {
      throw new Error("Folder not found in storage");
    }
    throw error;
  }
}

/**
 * Check if a ZIP file exists in the given folder path in Firebase Storage.
 * Searches one folder back (parent folder) from the given path.
 * Returns the ZIP file reference if found, null otherwise.
 */
async function checkForExistingZip(folderBasePath) {
  try {
    const storage = getStorage();
    
    // First, find where DICOM files are actually located by searching recursively
    const baseRef = storageRef(storage, folderBasePath);
    const allItems = await listAllFiles(baseRef);
    const dicomItems = allItems.filter((item) => {
      const isDcm = item.name.toLowerCase().endsWith(".dcm");
      const shouldExclude = shouldExcludeFromZip(item.name);
      return isDcm && !shouldExclude;
    });
    
    if (dicomItems.length === 0) {
      return null;
    }
    
    // Find the directory containing the most DICOM files (actual DICOM folder)
    const dicomDirectories = new Map();
    dicomItems.forEach((item) => {
      const dirPath = item.fullPath.substring(0, item.fullPath.lastIndexOf("/"));
      if (!dicomDirectories.has(dirPath)) {
        dicomDirectories.set(dirPath, []);
      }
      dicomDirectories.get(dirPath).push(item);
    });
    
    let actualDicomDir = folderBasePath;
    let maxDicomCount = 0;
    dicomDirectories.forEach((files, dirPath) => {
      if (files.length > maxDicomCount) {
        maxDicomCount = files.length;
        actualDicomDir = dirPath;
      }
    });
    
    // Now search one folder back from the actual DICOM directory
    const pathSegments = actualDicomDir.split('/').filter(seg => seg);
    if (pathSegments.length > 0) {
      pathSegments.pop(); // Remove last folder segment to go one folder back
    }
    const parentPath = pathSegments.join('/') + '/';
    
    const parentRef = storageRef(storage, parentPath);
    
    // List files in parent directory only (not recursive)
    const parentResult = await listAll(parentRef);
    
    // Look for any .zip files in the parent folder
    const zipItems = parentResult.items.filter((item) =>
      item.name.toLowerCase().endsWith(".zip")
    );
    
    if (zipItems.length > 0) {
      return zipItems[0];
    }

    return null;
  } catch (error) {
    console.error("Error checking for existing ZIP:", error);
    return null;
  }
}

/**
 * Download an existing ZIP file from Firebase Storage.
 * Returns a Blob suitable for uploading via multipart/form-data.
 */
async function downloadExistingZip(zipRef) {
  try {
    // const startTime = Date.now();

    const url = await getDownloadURL(zipRef);
    const response = await axios.get(url, {
      responseType: "blob",
      onDownloadProgress: (progressEvent) => {
        // Progress tracking removed for performance
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error downloading existing ZIP:", error);
    throw error;
  }
}

/**
 * Create a ZIP (Blob) of all DICOM files under the given folder_ path in Firebase Storage.
 * Saves the created ZIP to Firebase Storage with patient_id.zip naming.
 * Returns a Blob suitable for uploading via multipart/form-data.
 */
async function createDicomZipFromFirebase(folderBasePath, patientId = null) {
  const storage = getStorage();
  const baseRef = storageRef(storage, folderBasePath);
  const allItems = await listAllFiles(baseRef);
  const dicomItems = allItems.filter((item) => {
    const isDcm = item.name.toLowerCase().endsWith(".dcm");
    const shouldExclude = shouldExcludeFromZip(item.name);
    return isDcm && !shouldExclude;
  });
  if (dicomItems.length === 0) {
    throw new Error("No DICOM files found in storage to zip");
  }

  const zip = new JSZip();
  const CONCURRENCY = Math.min(6, dicomItems.length);
  let index = 0;

  async function worker() {
    while (true) {
      const i = index++;
      if (i >= dicomItems.length) break;
      const itemRef = dicomItems[i];
      const url = await getDownloadURL(itemRef);
      const resp = await axios.get(url, { responseType: "arraybuffer" });
      const relPath =
        itemRef.fullPath.startsWith(folderBasePath + "/")
          ? itemRef.fullPath.slice(folderBasePath.length + 1)
          : itemRef.name;
      zip.file(relPath, resp.data);
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  // Use STORE to minimize CPU; switch to DEFLATE for smaller uploads
  const zipBlob = await zip.generateAsync({
    type: "blob",
    compression: "STORE",
  });
  
  // Save the ZIP to Firebase Storage if patientId is provided
  if (patientId) {
    try {
      // Find the directory containing DICOM files (search recursively)
      // Group DICOM files by their parent directory
      const dicomDirectories = new Map();
      dicomItems.forEach((item) => {
        const dirPath = item.fullPath.substring(0, item.fullPath.lastIndexOf("/"));
        if (!dicomDirectories.has(dirPath)) {
          dicomDirectories.set(dirPath, []);
        }
        dicomDirectories.get(dirPath).push(item);
      });
      
      // Find the directory with the most DICOM files (likely the main DICOM folder)
      let targetDir = folderBasePath;
      let maxDicomCount = 0;
      dicomDirectories.forEach((files, dirPath) => {
        if (files.length > maxDicomCount) {
          maxDicomCount = files.length;
          targetDir = dirPath;
        }
      });
      
      // Save ZIP file one folder back (parent folder) from the directory containing DICOM files
      // Example: If DICOM files are at: Folder1/Folder2/Folder3/Folder4/*.dcm
      // Then save ZIP at: Folder1/Folder2/Folder3/file.zip (one folder back)
      const pathSegments = targetDir.split('/').filter(seg => seg);
      if (pathSegments.length > 0) {
        pathSegments.pop(); // Remove last folder segment to go one folder back
      }
      const parentPath = pathSegments.join('/') + '/';
      
      // Use folder name for ZIP filename to match AddPatient/AddImage naming convention
      // Extract folder name from the target directory path
      const folderName = targetDir.split('/').filter(seg => seg).pop() || 'folder';
      const safeFolderName = folderName.replace(/[/[\]?#]/g, "_");
      const zipFileName = `${safeFolderName}.zip`;
      const zipStoragePath = `${parentPath}${zipFileName}`;
      
      const zipRef = storageRef(storage, zipStoragePath);
      
      // Upload the ZIP to Firebase Storage
      await uploadBytes(zipRef, zipBlob);
    } catch (error) {
      // Don't throw error, just continue - we still want to return the ZIP blob
    }
  }
  
  return zipBlob;
}

/**
 * Get or create a DICOM ZIP from Firebase Storage.
 * First checks for existing ZIP files, if found downloads it,
 * otherwise creates a new ZIP from individual DICOM files and saves it to Firebase.
 */
async function getOrCreateDicomZip(folderBasePath, patientId = null) {
  try {
    // First check if a ZIP file already exists
    const existingZip = await checkForExistingZip(folderBasePath);

    if (existingZip) {
      return await downloadExistingZip(existingZip);
    } else {
      // Pass patientId to save the ZIP to Firebase
      return await createDicomZipFromFirebase(folderBasePath, patientId);
    }
  } catch (error) {
    console.error("Error in getOrCreateDicomZip:", error);
    throw error;
  }
}

export class AIModelHandler {
  constructor({
    modelId,
    adminName,
    updateTask,
    markAIResult,
    getDecryptedFolderPath,
    toast,
    costingType = "",
    handleWalletDeduction = null,
    triggeredByUser = "",
  }) {
    this.modelConfig = AI_MODELS[modelId];
    if (!this.modelConfig) {
      throw new Error(`Unknown modelId: ${modelId}`);
    }

    this.modelId = modelId;
    this.adminName = adminName;
    this.costingType = costingType;
    this.handleWalletDeduction = handleWalletDeduction;
    this.triggeredByUser = triggeredByUser;

    this.updateTask = updateTask;
    this.markAIResult = markAIResult;
    this.getDecryptedFolderPath = getDecryptedFolderPath;
    this.toast = toast;

    this.baseEndpoint = this.modelConfig.baseEndpoint;
    this.endpoints = this.modelConfig.endpoints;
    this.steps = this.modelConfig.steps || [];
    this.requiresPolling = !!this.modelConfig.requiresPolling;
    this.useUrlEncoded = !!this.modelConfig.useUrlEncoded;
    this.costKey = this.modelConfig.costKey || null;
  }

  async checkServerHealth() {
    // If there's no health endpoint defined, assume the server is healthy
    if (!this.endpoints.health) {
      return true;
    }

    const healthUrl = this.baseEndpoint + this.endpoints.health;
    try {
      const resp = await axios.get(healthUrl);
      return resp.status === 200;
    } catch (error) {
      console.error(
        `${this.modelConfig.displayName} server health check failed:`,
        error
      );
      return false;
    }
  }

  async pollStatus(patientId, taskId) {
    if (!this.endpoints.status) return;
    const urlTemplate = this.endpoints.status;
    let tries = 0;
    const maxTries = 120; // Increased from 40 to allow for longer processing time

    while (tries < maxTries) {
      tries++;
      await delay(2000);

      const statusUrl =
        this.baseEndpoint + urlTemplate.replace(":taskId", taskId);
      try {
        const res = await axios.get(statusUrl);
        const status = (res.data && res.data.status) || "Processing";

        this.updateTask(patientId, { status });
        if (status.toLowerCase() === "done") {
          return true;
        }
        if (status.toLowerCase().includes("failed")) {
          throw new Error("Model processing failed on server");
        }
      } catch (err) {
        console.error("Error during pollStatus:", err);
        this.updateTask(patientId, { status: "Failed" });
        throw err;
      }
    }
    throw new Error("Polling timed out after too many attempts");
  }

  async downloadAndUploadToFirebase(taskId, patientId) {
    const downloadUrl =
      this.baseEndpoint + this.endpoints.download.replace(":taskId", taskId);

    try {
      // Download the file from our backend
      const response = await axios({
        url: downloadUrl,
        method: "GET",
        responseType: "blob",
      });

      // Get the filename from the Content-Disposition header or use a default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "processed_output.dcm";
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
          contentDisposition
        );
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      // Get a reference to the patient's data to find the latest folder_
      const patientRef = ref(
        db,
        `superadmin/admins/${this.adminName}/patients/${patientId}`
      );
      const snapshot = await get(patientRef);
      const patientData = snapshot.val();

      let folderPath = "";
      if (patientData.Version === "03" && patientData.uploads) {
        const uploadKeys = Object.keys(patientData.uploads)
          .filter((key) => key.toLowerCase().startsWith("scan"))
          .sort((a, b) => {
            const numA = parseInt(a.replace(/\D/g, ""));
            const numB = parseInt(b.replace(/\D/g, ""));
            return numB - numA; // Sort in descending order
          });

        for (const key of uploadKeys) {
          const upload = patientData.uploads[key];
          if (upload.FolderPath) {
            const pathSegments = upload.FolderPath.split("/");
            for (const segment of pathSegments.reverse()) {
              if (segment.toLowerCase().includes("folder_")) {
                folderPath = segment;
                break;
              }
            }
            if (folderPath) break;
          }
        }
      } else {
        if (patientData.FolderPath) {
          const pathSegments = patientData.FolderPath.split("/");
          for (const segment of pathSegments.reverse()) {
            if (segment.toLowerCase().includes("folder_")) {
              folderPath = segment;
              break;
            }
          }
        }
      }

      if (!folderPath) {
        const decryptedPath = this.getDecryptedFolderPath(patientData);
        if (decryptedPath) {
          const pathSegments = decryptedPath.split("/");
          for (const segment of pathSegments.reverse()) {
            if (segment.toLowerCase().includes("folder_")) {
              folderPath = segment;
              break;
            }
          }
        }
      }

      if (!folderPath) {
        throw new Error("No valid folder_ path found in the complete path");
      }

      // Create a reference to Firebase Storage with the new path structure
      const storage = getStorage();
      const storagePath = `superadmin/admins/${this.adminName}/patients/${patientId}/${folderPath}/AI_Results`;
      const fileRef = storageRef(storage, `${storagePath}/${filename}`);

      // Upload the file
      await uploadBytes(fileRef, response.data);
      return true;
    } catch (error) {
      console.error("Error in downloadAndUploadToFirebase:", error);
      throw error;
    }
  }

  async runModel(
    patientId,
    patient,
    { costValue = 0, walletBalance = 0, costingType = "" } = {}
  ) {
    // 1) Check server health
    const isHealthy = await this.checkServerHealth();
    if (!isHealthy) {
      this.updateTask(patientId, { status: "Server down" });
      if (this.toast) {
        this.toast.error(`${this.modelConfig.displayName} server unavailable`, {
          position: "top-center",
        });
      }
      throw new Error("Server not available");
    }

    // 1A) If Prepaid model => do a quick check if user can afford
    if (
      this.costKey &&
      costingType.toLowerCase() === "prepaid" &&
      costValue > 0
    ) {
      if (walletBalance < costValue) {
        throw new Error("Insufficient funds");
      }
    }

    // 2) Build base params
    if (!this.modelConfig.processParams) {
      throw new Error("No processParams defined for this model");
    }
    const baseParams = this.modelConfig.processParams(
      patientId,
      this.adminName,
      this.getDecryptedFolderPath(patient)
    );
    let currentParams = { ...baseParams };



    // 3) Run steps in sequence

    for (const step of this.steps) {

      this.updateTask(patientId, { status: `In-Progress: ${step}` });

      // Special handling for TB model (now renamed to CXR_TB)
      if (this.modelId === "CXR_TB" && step === "process") {
        try {
          // Get the full path from patient data
          const fullPath = this.getDecryptedFolderPath(patient);
          if (!fullPath) {
            throw new Error("No valid folder path found in patient data");
          }

          // Get the image file from the folder path
          const imageFile = await getDicomFileFromPath(fullPath);

          // Determine input file type
          let inputType = "dcm";
          if (imageFile.type.includes("png")) inputType = "png";
          if (imageFile.type.includes("jpeg") || imageFile.type.includes("jpg"))
            inputType = "jpg";

          // Create FormData and append the file
          const formData = new FormData();
          formData.append("file", imageFile, "image." + inputType);

          // Make the API call
          // Route: DICOM -> /predict, Images (png/jpg/jpeg) -> /imagexai-image (if defined)
          const endpointPath =
            inputType === "dcm"
              ? this.endpoints.process
              : (this.endpoints.processImage || this.endpoints.process);
          const response = await axios.post(
            this.baseEndpoint + endpointPath,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response.data) {
            const { prediction, visualization_image_base64, dicom_base64 } =
              response.data;
            const storage = getStorage();
            const aiResultsPath = `${fullPath}/AI_Results`;
            let fileSaved = false;

            // Save only the output file matching the input type
            if (inputType === "dcm" && dicom_base64) {
              const dicomRef = storageRef(
                storage,
                `${aiResultsPath}/tb_detection_result.dcm`
              );
              const dicomBlob = base64ToBlob(dicom_base64, "application/dicom");
              await uploadBytes(dicomRef, dicomBlob);
              fileSaved = true;
            } else if (
              (inputType === "png" || inputType === "jpg") &&
              visualization_image_base64
            ) {
              const ext = inputType === "png" ? "png" : "jpg";
              const visRef = storageRef(
                storage,
                `${aiResultsPath}/tb_detection_result.${ext}`
              );
              const visBlob = base64ToBlob(
                visualization_image_base64,
                `image/${ext}`
              );
              await uploadBytes(visRef, visBlob);
              fileSaved = true;
            }

            // Only deduct wallet if file was saved
            if (fileSaved) {
              await this.handleWalletDeduction(
                this.costKey,
                patient,
                this.adminName
              );
            }

            // Update task status with the prediction
            this.updateTask(patientId, {
              status: `Done - ${prediction}`,
            });

            continue; // Move to next step
          }
        } catch (err) {
          console.error("Error in TB detection:", err);
          this.updateTask(patientId, { status: "Failed" });
          throw err;
        }
      }

      // Special handling for CXR-TB2 model
      if (this.modelId === "CXR_TB2" && step === "predict") {
        try {
          // Get the full path from patient data
          const fullPath = this.getDecryptedFolderPath(patient);
          if (!fullPath) {
            throw new Error("No valid folder path found in patient data");
          }

          // Get the image file from the folder path
          const imageFile = await getDicomFileFromPath(fullPath);

          // Determine input file type
          let inputType = "dcm";
          if (imageFile.type.includes("png")) inputType = "png";
          if (imageFile.type.includes("jpeg") || imageFile.type.includes("jpg"))
            inputType = "jpg";

          // Create FormData and append the file
          const formData = new FormData();
          formData.append("file", imageFile, "image." + inputType);

          // Make the API call to NewTB endpoint
          const response = await axios.post(
            this.baseEndpoint + this.endpoints.predict,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response.data) {
            const { predicted_class, confidence, probabilities, dicom_image_png_base64 } = response.data;
            const storage = getStorage();
            const aiResultsPath = `${fullPath}/AI_Results`;
            let fileSaved = false;

            // Prepare prediction metadata to embed in image
            const predictionMetadata = {
              predicted_class,
              confidence,
              probabilities,
              timestamp: new Date().toISOString(),
              model: "CXR-TB2"
            };

            // Save the result image with embedded metadata
            if (inputType === "dcm" && dicom_image_png_base64) {
              // For DICOM input, save the PNG preview with embedded metadata
              const pngRef = storageRef(
                storage,
                `${aiResultsPath}/cxr_tb2_result.png`
              );
              const pngBlob = base64ToBlob(dicom_image_png_base64.split(',')[1], "image/png");
              
              // Embed metadata into PNG image
              const imageWithMetadata = await this.embedMetadataInImage(pngBlob, predictionMetadata, 'png');
              await uploadBytes(pngRef, imageWithMetadata);
              fileSaved = true;
            } else if ((inputType === "png" || inputType === "jpg")) {
              // For image input, embed metadata into the original image
              const ext = inputType === "png" ? "png" : "jpg";
              const resultRef = storageRef(
                storage,
                `${aiResultsPath}/cxr_tb2_result.${ext}`
              );
              
              // Embed metadata into the image
              const imageWithMetadata = await this.embedMetadataInImage(imageFile, predictionMetadata, ext);
              await uploadBytes(resultRef, imageWithMetadata);
              fileSaved = true;
            }

            // Only deduct wallet if file was saved
            if (fileSaved) {
              await this.handleWalletDeduction(
                this.costKey,
                patient,
                this.adminName
              );
            }

            // Handle postpaid logging for CXR-TB2
            if (this.costingType && this.costingType.toLowerCase() === "postpaid") {
              // Postpaid logging will be handled by executeAIModel
            }

            // Update task status with the prediction
            this.updateTask(patientId, {
              status: `Done - ${predicted_class} (${(confidence * 100).toFixed(1)}%)`,
            });

            continue; // Move to next step
          }
        } catch (err) {
          console.error("Error in CXR-TB2 detection:", err);
          this.updateTask(patientId, { status: "Failed" });
          throw err;
        }
      }

      // Special handling for CXR-Multiclass model
      if (this.modelId === "CXR_MULTICLASS" && step === "generateReport") {
        try {
          // Get the full path from patient data
          const fullPath = this.getDecryptedFolderPath(patient);
          if (!fullPath) {
            throw new Error("No valid folder path found in patient data");
          }

          // Get the image file from the folder path
          const imageFile = await getDicomFileFromPath(fullPath);

          // Create FormData and append the file with grounding parameter
          const formData = new FormData();
          formData.append("frontal_image", imageFile, imageFile.name || "chest_xray.jpg"); // API expects 'frontal_image'
          formData.append("grounding", "true"); // Enable grounding for bounding boxes

          console.log("AIModelHandler - Making API call to generate-report endpoint with grounding=true");
          console.log("AIModelHandler - Full URL:", this.baseEndpoint + this.endpoints.generateReport);
          console.log("AIModelHandler - FormData file:", imageFile.name, imageFile.type, imageFile.size);
          
          // Make the API call
          const response = await axios.post(
            this.baseEndpoint + this.endpoints.generateReport,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          console.log("AIModelHandler - Received response with grounding data");

          if (response.data && response.data.success) {
            const { 
              report, 
              annotated_image_base64, 
              bounding_boxes, 
              debug_info, 
              raw_result,
              grouped_boxes 
            } = response.data;
            
            console.log("AIModelHandler - Extracted grounding report data:", {
              report,
              boundingBoxCount: bounding_boxes?.length || 0,
              hasAnnotatedImage: !!annotated_image_base64
            });

            const storage = getStorage();
            const aiResultsPath = `${fullPath}/AI_Results`;

            // Save annotated image with color-coded bounding boxes (already done by backend)
            if (annotated_image_base64) {
              const annotatedImageRef = storageRef(
                storage,
                `${aiResultsPath}/cxr_multiclass_annotated.png`
              );
              const annotatedImageBlob = base64ToBlob(annotated_image_base64, "image/png");
              await uploadBytes(annotatedImageRef, annotatedImageBlob);
              console.log("AIModelHandler - Backend-colored annotated image saved to Firebase Storage");
            }

            // Save AI_Report data to the latest scan in Firebase Realtime Database
            await this.saveAIReportToLatestScan(patientId, {
              report,
              bounding_boxes,
              debug_info,
              raw_result,
              grouped_boxes,
              annotated_image_base64
            });

            // Generate PDF report automatically
            try {
              console.log("AIModelHandler - Generating PDF report for patient:", patientId);
              
              // Get patient data for PDF
              const patientRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}`);
              const patientSnapshot = await get(patientRef);
              const patientInfo = patientSnapshot.val();
              
              if (patientInfo) {
                // Decrypt patient data if needed
                const CryptoJS = require('crypto-js');
                const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
                const decryptData = (encryptedData) => {
                  try {
                    const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
                    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
                    return decryptedData || encryptedData;
                  } catch (error) {
                    return encryptedData;
                  }
                };

                // Prepare patient data for PDF
                console.log("AIModelHandler - Raw patient info fields:", {
                  hasFullName: !!patientInfo.FullName,
                  FullNameValue: patientInfo.FullName,
                  hasID: !!patientInfo.ID,
                  hasGender: !!patientInfo.Gender,
                  hasAge: !!patientInfo.Age
                });

                const decryptedName = patientInfo.FullName ? decryptData(patientInfo.FullName) : null;
                console.log("AIModelHandler - Decrypted name result:", decryptedName);

                const pdfPatientData = {
                  name: decryptedName && decryptedName.trim() !== '' ? decryptedName : 'N/A',
                  id: patientInfo.ID ? decryptData(patientInfo.ID) : 'N/A',
                  gender: patientInfo.Gender ? decryptData(patientInfo.Gender) : 'N/A',
                  age: patientInfo.Age ? decryptData(patientInfo.Age) : 'N/A',
                  clinicalHistory: patientInfo.Clinical_Textarea ? decryptData(patientInfo.Clinical_Textarea) : 'Not provided'
                };

                console.log("AIModelHandler - Final PDF patient data:", pdfPatientData);

                // Get original image for fallback
                let originalImageBase64 = null;
                try {
                  const originalImageFile = await getDicomFileFromPath(fullPath);
                  const originalImageBlob = new Blob([originalImageFile]);
                  originalImageBase64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result.split(',')[1]);
                    reader.readAsDataURL(originalImageBlob);
                  });
                } catch (imgErr) {
                  console.warn("AIModelHandler - Could not load original image for PDF:", imgErr);
                }

                // Parse grouped_boxes for PDF
                let groupedBoxesForPdf = {};
                if (grouped_boxes && Array.isArray(grouped_boxes)) {
                  grouped_boxes.forEach((item, index) => {
                    if (Array.isArray(item) && item.length >= 3) {
                      const [explanation, , color] = item; // Skip boxes element (unused)
                      if (color !== null && color !== undefined) {
                        groupedBoxesForPdf[index] = {
                          Explanation: explanation,
                          Color: String(color) // Ensure it's a string
                        };
                      } else {
                        groupedBoxesForPdf[index] = {
                          Explanation: explanation
                        };
                      }
                    }
                  });
                }

                // Generate PDF
                const pdfBlob = await generateCXRMulticlassReport(
                  pdfPatientData,
                  annotated_image_base64,
                  originalImageBase64,
                  groupedBoxesForPdf
                );

                // Save PDF to Firebase Storage (following AddPatient pattern)
                const pdfFileName = `cxr_multiclass_report_${Date.now()}.pdf`;
                const pdfStoragePath = `superadmin/admins/${this.adminName}/patients/${patientId}/${pdfFileName}`;
                const pdfRef = storageRef(storage, pdfStoragePath);
                await uploadBytes(pdfRef, pdfBlob);
                
                // Get download URL
                const pdfDownloadURL = await getDownloadURL(pdfRef);
                console.log("AIModelHandler - PDF report saved to Firebase Storage");

                // Encrypt the path and URL like AddPatient does
                const encryptData = (data) => {
                  try {
                    return CryptoJS.AES.encrypt(data, secretKey).toString();
                  } catch (error) {
                    console.error('Encryption failed:', error);
                    return data;
                  }
                };

                const encryptedPdfPath = encryptData(pdfStoragePath);
                const encryptedPdfURL = encryptData(pdfDownloadURL);

                // Find the latest scan and save PDF info there (like AddPatient does with reports)
                if (patientInfo.Version === "03" && patientInfo.uploads) {
                  const uploads = patientInfo.uploads;
                  const scanKeys = Object.keys(uploads)
                    .filter((key) => key.toLowerCase().includes("scan"))
                    .map((key) => ({
                      key,
                      number: parseInt(key.replace(/\D/g, "")) || 0,
                    }))
                    .sort((a, b) => b.number - a.number);

                  if (scanKeys.length > 0) {
                    const latestScanKey = scanKeys[0].key;
                    
                    // Save AI Report PDF info to the latest scan (using EXACT same field names as AddPatient)
                    const dicomReportRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}/uploads/${latestScanKey}/Dicom_Report`);
                    const pathReportRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}/uploads/${latestScanKey}/PathReport`);
                    const dicomReportURLRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}/uploads/${latestScanKey}/Dicom_ReportURL`);
                    
                    await Promise.all([
                      set(dicomReportRef, pdfFileName),
                      set(pathReportRef, encryptedPdfPath),
                      set(dicomReportURLRef, encryptedPdfURL)
                    ]);
                    
                    console.log("AIModelHandler - PDF report info saved to Realtime Database");
                  }
                }
              }
            } catch (pdfErr) {
              console.error("AIModelHandler - Error generating PDF report:", pdfErr);
              // Don't throw - continue even if PDF generation fails
            }

            // Update task status with report summary
            const reportSummary = report.split('\n')[0] || 'Report generated';
            this.updateTask(patientId, {
              status: `Done - ${reportSummary.substring(0, 50)}...`,
            });

            continue; // Move to next step
          } else {
            throw new Error("Invalid response from generate-report endpoint");
          }
        } catch (err) {
          console.error("Error in CXR-Multiclass generate-report:", err);
          console.error("Error details:", {
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data,
            url: this.baseEndpoint + this.endpoints.generateReport
          });
          this.updateTask(patientId, { status: "Failed - " + (err.response?.status || "Network Error") });
          throw err;
        }
      }

      // Special handling for XRAY_FRACTURE model - download file from Firebase and send to /get_path endpoint
      if (step === "process" && this.modelId === "XRAY_FRACTURE") {
        try {
          this.updateTask(patientId, { status: "Processing bone fracture detection" });

          // Get the image file from the folder path
          const imageFile = await getDicomFileFromPath(currentParams.decryptedFolderPath);
          
          // Send file to /get_path endpoint
          const formData = new FormData();
          formData.append("file", imageFile, imageFile.name || "image.dcm");

          const response = await axios.post(
            `${this.baseEndpoint + this.endpoints.process}?adminName1=${encodeURIComponent(currentParams.adminName || "")}&patientId=${encodeURIComponent(currentParams.patientId || "")}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response.data && response.data.task_id) {
            // Store task_id for polling and download
            currentParams.task_id = response.data.task_id;
            this.updateTask(patientId, { status: "Processing started, waiting for completion..." });
            
            // Poll for completion before proceeding to download step
            await this.pollStatus(patientId, currentParams.task_id);
          }

          continue;
        } catch (err) {
          console.error("Error in XRAY_FRACTURE processing:", err);
          this.updateTask(patientId, { status: "Failed" });
          throw err;
        }
      }

      // Special handling for download step in XRay Fracture model
      if (step === "download" && this.modelId === "XRAY_FRACTURE") {
        try {
          await this.downloadAndUploadToFirebase(
            currentParams.task_id,
            patientId
          );
          continue;
        } catch (err) {
          console.error("Error in download step:", err);
          throw err;
        }
      }

      // Special handling for 3D-Abdomen and Liver-Segmentation model
      if (step === "glb" && (this.modelId === "THREED_ABDOMEN" || this.modelId === "LIVER_SEGMENTATION")) {
        try {
          this.updateTask(patientId, { status: "Generating 3D model" });

          // Always call the /glb endpoint directly with FormData
          const formData = new FormData();
          formData.append("merged_nifti", currentParams.merged_nifti);
          formData.append("task_id", currentParams.task_id);

          const glbUrl = this.baseEndpoint + this.endpoints.glb.replace(":taskId", "");
          const glbResponse = await axios.post(glbUrl, formData, {
            responseType: "arraybuffer",
          });

          if (glbResponse && glbResponse.data) {
            // Convert binary data to blob
            const glbBlob = new Blob([glbResponse.data], { type: "model/gltf-binary" });

            // Create filename for the .glb file
            const glbFilename = this.modelId === "LIVER_SEGMENTATION"
              ? `liver_segmentation_${patientId}.glb`
              : `3d_model_${patientId}.glb`;

            // Upload to Firebase Storage under patient path
            const patientRef = ref(db, `superadmin/admins/${this.adminName}/patients`);
            const snapshot = await get(patientRef);
            const patients = snapshot.val() || {};

            // Find the patient key
            let patientKey = null;
            for (const [key, value] of Object.entries(patients)) {
              if (value.ID === patientId || key === patientId) {
                patientKey = key;
                break;
              }
            }

            if (!patientKey) {
              throw new Error(`Patient key not found for ID: ${patientId}`);
            }

            // Upload to patient's specific path
            const glbStoragePath = `superadmin/admins/${this.adminName}/patients/${patientKey}/${glbFilename}`;
            const glbRef = storageRef(getStorage(), glbStoragePath);

            await uploadBytes(glbRef, glbBlob);

            this.updateTask(patientId, { status: "Uploading 3D model to storage" });

            // Get download URL and store in database
            const downloadURL = await getDownloadURL(glbRef);

            // Encrypt the data before saving
            const CryptoJS = require('crypto-js');
            const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
            const encryptData = (data) => {
              try {
                return CryptoJS.AES.encrypt(data, secretKey).toString();
              } catch (error) {
                console.error('Encryption failed:', error);
                return data;
              }
            };
            const encryptedURL = encryptData(downloadURL);
            const encryptedPath = encryptData(glbStoragePath);

            // Check if patient has Version 03 with uploads structure
            const specificPatientRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientKey}`);
            const patientSnapshot = await get(specificPatientRef);
            const patientData = patientSnapshot.val();

            if (patientData && patientData.Version === "03" && patientData.uploads) {
              // For Version 03 patients, find the latest scan and save there
              const uploads = patientData.uploads;
              const scanKeys = Object.keys(uploads)
                .filter((key) => key.toLowerCase().startsWith("scan"))
                .map((key) => ({
                  key,
                  number: parseInt(key.replace("Scan", "").trim(), 10),
                }))
                .sort((a, b) => b.number - a.number);

              if (scanKeys.length > 0) {
                const latestScanKey = scanKeys[0].key;
                // Save to the latest scan in uploads structure
                const scanPath3DRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientKey}/uploads/${latestScanKey}/Path3D`);
                const scanDicom3DURLRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientKey}/uploads/${latestScanKey}/Dicom_3DURL`);
                await Promise.all([
                  set(scanPath3DRef, encryptedPath),
                  set(scanDicom3DURLRef, encryptedURL)
                ]);
              } else {
                // Fallback to patient level if no scans found
                const path3DRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientKey}/Path3D`);
                const dicom3DURLRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientKey}/Dicom_3DURL`);
                await Promise.all([
                  set(path3DRef, encryptedPath),
                  set(dicom3DURLRef, encryptedURL)
                ]);
              }
            } else {
              // For legacy patients, save directly to patient level
              const path3DRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientKey}/Path3D`);
              const dicom3DURLRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientKey}/Dicom_3DURL`);
              await Promise.all([
                set(path3DRef, encryptedPath),
                set(dicom3DURLRef, encryptedURL)
              ]);
            }

            // Deduct wallet for 3D price (regardless of whether contouring ran)
            await this.handleWalletDeduction(this.costKey, patient, this.adminName);

            this.updateTask(patientId, { status: "Done" });

            if (this.toast) {
              this.toast.success("3D model generated successfully", {
                position: "top-center",
              });
            }
          }

          continue;
        } catch (err) {
          console.error(`Error in ${this.modelId} 3D generation:`, err);
          this.updateTask(patientId, { status: "Failed" });
          throw err;
        }
      }

      // Special handling for download step in TUMOR_ABDOMEN model
      if (step === "download" && this.modelId === "TUMOR_ABDOMEN") {
        try {
          // Start timing for file download
          // const downloadStartTime = Date.now();
          
          // Create ZIP from Firebase Storage
          const folderPath = getFolderBasePath(currentParams.decryptedFolderPath);
          if (!folderPath) {
            throw new Error("No valid folder path found for creating ZIP");
          }
          
          // Check if we've already created a ZIP for this patient (performance optimization)
          if (!currentParams.cachedDicomZip) {
            // Get patient ID for ZIP naming (without random suffix for storage)
            const reqBodyForZip = this.modelConfig.buildRequestBody(step, currentParams);
            // Remove random suffix (last 4 digits) from patient_id for Firebase storage
            const basePatientId = reqBodyForZip.patient_id ? reqBodyForZip.patient_id.replace(/\d{4}$/, '') : patientId;
            
            const dicomZipBlob = await getOrCreateDicomZip(folderPath, basePatientId);
            currentParams.cachedDicomZip = dicomZipBlob; // Cache the ZIP for potential reuse
            
            // Calculate and log download time
            // const downloadEndTime = Date.now();
            // const downloadDuration = (downloadEndTime - downloadStartTime) / 1000; // Convert to seconds
            // const minutes = Math.floor(downloadDuration / 60);
            // const seconds = (downloadDuration % 60).toFixed(1);
            
          }
          
          // Store the Firebase folder path for later use
          currentParams.firebaseFolderPath = folderPath;
          
          // Get request body parameters (which includes the unique patient ID)
          const requestBody = this.modelConfig.buildRequestBody(step, currentParams);
          
          // Create FormData with the ZIP file (ignore useUrlEncoded for download step)
          const formData = new FormData();
          const zipFilename = generateZipFilename(requestBody, true);
          formData.append("zip_file", currentParams.cachedDicomZip, zipFilename);
          formData.append("patient_id", requestBody.patient_id);
          
          // Call /download-dicom with FormData (always use multipart for this step)
          const response = await axios.post(
            this.baseEndpoint + this.endpoints.download,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          
          if (response.data && response.data.dicom_path) {
            currentParams.dicom_path = response.data.dicom_path;
            continue;
          } else {
            throw new Error("No DICOM path returned from download-dicom");
          }
        } catch (err) {
          console.error("Error in download step for TUMOR_ABDOMEN:", err);
          this.updateTask(patientId, { status: "Failed at download" });
          throw err;
        }
      }

      // Special handling for dicomzip step in LIVER_SEGMENTATION model
      if (step === "dicomzip" && this.modelId === "LIVER_SEGMENTATION") {
        try {
          // Start timing for file download
          // const downloadStartTime = Date.now();
          
          // Create ZIP from Firebase Storage
          const folderPath = getFolderBasePath(currentParams.decryptedFolderPath);
          if (!folderPath) {
            throw new Error("No valid folder path found for creating ZIP");
          }
          
          // Check if we've already created a ZIP for this patient (performance optimization)
          if (!currentParams.cachedDicomZip) {
            // Get patient ID for ZIP naming (without random suffix for storage)
            const reqBodyForZip = this.modelConfig.buildRequestBody(step, currentParams);
            // Remove random suffix (last 4 digits) from task_id for Firebase storage
            const basePatientId = reqBodyForZip.task_id ? reqBodyForZip.task_id.replace(/\d{4}$/, '') : patientId;
            
            const dicomZipBlob = await getOrCreateDicomZip(folderPath, basePatientId);
            currentParams.cachedDicomZip = dicomZipBlob; // Cache the ZIP for potential reuse
            
            // Calculate and log download time
            // const downloadEndTime = Date.now();
            // const downloadDuration = (downloadEndTime - downloadStartTime) / 1000; // Convert to seconds
            // const minutes = Math.floor(downloadDuration / 60);
            // const seconds = (downloadDuration % 60).toFixed(1);
            
          }
          
          // Get request body parameters (including task_id)
          const requestBody = this.modelConfig.buildRequestBody(step, currentParams);
          
          // Create FormData with the ZIP file
          const formData = new FormData();
          const zipFilename = generateZipFilename(requestBody, true);
          formData.append("zip_file", currentParams.cachedDicomZip, zipFilename);
          formData.append("task_id", requestBody.task_id);
          
          // Call /dicomzip with FormData
          const response = await axios.post(
            this.baseEndpoint + this.endpoints.dicomzip,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          
          if (response.data && response.data.dicom_path) {
            currentParams.dicom_path = response.data.dicom_path;
            currentParams.file_count = response.data.file_count;
            continue;
          } else {
            throw new Error("No dicom_path returned from dicomzip");
          }
        } catch (err) {
          console.error("Error in dicomzip step for LIVER_SEGMENTATION:", err);
          this.updateTask(patientId, { status: "Failed at dicomzip" });
          throw err;
        }
      }

      // Special handling for segment step in LIVER_SEGMENTATION model
      if (step === "segment" && this.modelId === "LIVER_SEGMENTATION") {
        try {
          // Get request body parameters (including dicom_path and task_id)
          const requestBody = this.modelConfig.buildRequestBody(step, currentParams);
          
          // Ensure we have dicom_path from previous step
          if (!requestBody.dicom_path) {
            throw new Error("No dicom_path available for segmentation");
          }
          
          // Create URL-encoded form data for segment endpoint
          const formData = new URLSearchParams();
          formData.append("dicom_path", requestBody.dicom_path);
          formData.append("task_id", requestBody.task_id);
          
          // Call /segment with URL-encoded data
          const response = await axios.post(
            this.baseEndpoint + this.endpoints.segment,
            formData,
            { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
          );
          
          if (response.data && response.data.output_files) {
            currentParams.output_files = response.data.output_files;
            currentParams.liver_segments = response.data.output_files.liver_segments;
            currentParams.liver_vessels = response.data.output_files.liver_vessels;
            continue;
          } else {
            throw new Error("No output files returned from segment");
          }
        } catch (err) {
          console.error("Error in segment step for LIVER_SEGMENTATION:", err);
          this.updateTask(patientId, { status: "Failed at segment" });
          throw err;
        }
      }

      // Special handling for segmentation step in Abdomen Tumor Analysis
      if (step === "segmentation" && this.modelId === "TUMOR_ABDOMEN") {
        try {
          this.updateTask(patientId, { status: "Processing segmentation" });

          let requestBody = this.modelConfig.buildRequestBody
            ? this.modelConfig.buildRequestBody(step, currentParams)
            : currentParams;

          const endpointForStep = this.endpoints[step];
          const fullUrl = this.baseEndpoint + endpointForStep;

          // /dicomseg endpoint expects multipart/form-data with ZIP file
          if (!currentParams.cachedDicomZip) {
            throw new Error("No cached DICOM ZIP found for segmentation step");
          }
          
          const formData = new FormData();
          const zipFilename = generateZipFilename(requestBody, true);
          formData.append("zip_file", currentParams.cachedDicomZip, zipFilename);
          formData.append("seg_nifti", requestBody.seg_nifti || currentParams.seg_nifti);
          formData.append("patient_id", requestBody.patient_id);
          
          let dataToSend = formData;
          let headers = { "Content-Type": "multipart/form-data" };

          


          // Log the outgoing request
          if (dataToSend instanceof FormData) {
            // FormData can't be directly stringified, so log its entries
            const formDataObj = {};
            for (let pair of dataToSend.entries()) {
              formDataObj[pair[0]] = pair[1];
            }
           
          } else {
          
          }

          // Make a request with responseType: 'arraybuffer' to get the raw binary data
          const response = await axios.post(fullUrl, dataToSend, {
            headers,
            responseType: "arraybuffer", // Important: Get raw binary data
            // This ensures we see all headers in the response
            transformResponse: [(data) => data],
          });

         

          if (response.data) {
            // Get the filename from the Content-Disposition header which contains the backend-generated name
            let filename;


            // There are two possible ways the filename might be in the headers depending on the server:
            // 1. In content-disposition header with format: attachment; filename="dicom-seg-8.dcm"
            // 2. In x-filename or other custom header

            // Try to extract from content-disposition header
            if (response.headers && response.headers["content-disposition"]) {
              const contentDisposition =
                response.headers["content-disposition"];
              

              // This regex is more permissive to handle different quoting styles
              const filenameMatch = contentDisposition.match(
                /filename=["']?([^"'\s;]+)["']?/i
              );
              if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].trim();
              }
            }

            // If we couldn't get the filename from the headers, generate one that matches the backend pattern
            if (!filename) {
              console.warn("Could not extract filename from headers");
              // Generate a random number between 1-9 to match the backend pattern
              const rand = Math.floor(Math.random() * 9) + 1;
              filename = `dicom-seg-${rand}.dcm`;
            }

            // Convert the arraybuffer to a Blob and then to a File with the original filename
            const blob = new Blob([response.data], {
              type: "application/dicom",
            });
        

            const segmentationFile = new File(
              [blob],
              filename, // This will be exactly as generated by the backend (e.g., dicom-seg-8.dcm)
              { type: "application/dicom" }
            );
            // Get the Firebase storage reference
            const storage = getStorage();

            // Get the base folder path - we need this for Firebase storage upload
            // We stored it in _storedFirebasePath during the download step
            const folderBasePath =
              currentParams._storedFirebasePath ||
              currentParams.firebase_path ||
              currentParams.decryptedFolderPath;
            if (!folderBasePath) {
              throw new Error("Firebase path not found in parameters");
            }

            this.updateTask(patientId, {
              status: "Uploading segmentation file to storage",
            });

            // List all folders containing .dcm files
            const baseRef = storageRef(storage, folderBasePath);
            const allItems = await listAllFiles(baseRef);

            // Group items by folder
            const folderMap = new Map();
            allItems.forEach((item) => {
              if (item.name.toLowerCase().endsWith(".dcm")) {
                const folderPath = item.fullPath.substring(
                  0,
                  item.fullPath.lastIndexOf("/")
                );
                if (!folderMap.has(folderPath)) {
                  folderMap.set(folderPath, []);
                }
                folderMap.get(folderPath).push(item);
              }
            });

            // Upload the segmentation file to each folder that contains DICOM files
            const uploadPromises = [];
            folderMap.forEach((items, folderPath) => {
              // Use the original filename for uploading to Firebase
              const segFileRef = storageRef(
                storage,
                `${folderPath}/${segmentationFile.name}`
              );
              uploadPromises.push(uploadBytes(segFileRef, segmentationFile));
            });

            await Promise.all(uploadPromises);

            // Update currentParams to include reference to the segmentation file with original filename
            currentParams = {
              ...currentParams,
              segmentation_file: segmentationFile.name,
            };
          }

          continue;
        } catch (err) {
          console.error("Error in segmentation step:", err);
          this.updateTask(patientId, { status: "Failed" });
          throw err;
        }
      }

                  // Special handling for Contour-HaN model
           if (this.modelId === "Contour-HaN") {
          if (step === "health") {
            // Health check is already performed at the start, just continue
            continue;
          }
          if (step === "dicomToNifti") {
            try {
              // Start timing for file download
              // const downloadStartTime = Date.now();
              
              // Create ZIP from Firebase Storage
              const folderPath = getFolderBasePath(currentParams.decryptedFolderPath);
              
              // Get patient ID for ZIP naming (without random suffix for storage)
              const reqBodyForZip = this.modelConfig.buildRequestBody(step, currentParams);
              // Remove random suffix (last 4 digits) from patient_id for Firebase storage
              const basePatientId = reqBodyForZip.patient_id ? reqBodyForZip.patient_id.replace(/\d{4}$/, '') : patientId;
              
              const dicomZipBlob = await getOrCreateDicomZip(folderPath, basePatientId);
              
              // Calculate and log download time
              // const downloadEndTime = Date.now();
              // const downloadDuration = (downloadEndTime - downloadStartTime) / 1000; // Convert to seconds
              // const minutes = Math.floor(downloadDuration / 60);
              // const seconds = (downloadDuration % 60).toFixed(1);
              
              
              // Store the Firebase folder path for later use
              currentParams.firebaseFolderPath = folderPath;
              //currentParams.downloadTime = downloadDuration;
              
              // Get request body parameters
              const requestBody = this.modelConfig.buildRequestBody(step, currentParams);
              
              // Create FormData with the ZIP file
              const formData = new FormData();
              const zipFilename = generateZipFilename(requestBody, true);
              formData.append("dicom_zip", dicomZipBlob, zipFilename);
              formData.append("patient_id", requestBody.patient_id);
              
              // Call /dicom-to-nifti with FormData
              const response = await axios.post(
                this.baseEndpoint + this.endpoints.dicomToNifti,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
              );
              
              if (response.data && response.data.nifti_file) {
                currentParams.niftiFile = response.data.nifti_file;
                continue;
              } else {
                throw new Error("No NIfTI file returned from dicom-to-nifti");
              }
            } catch (err) {
              console.error("Error in dicom-to-nifti step for Contour-HaN:", err);
              this.updateTask(patientId, { status: "Failed at dicom-to-nifti" });
              throw err;
            }
          }
          if (step === "segment") {
            try {
              // Step 2: Call /segment
              const requestBody = this.modelConfig.buildRequestBody(step, currentParams);
              const response = await axios.post(
                this.baseEndpoint + this.endpoints.segment,
                toUrlSearchParams(requestBody),
                { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
              );
              if (response.data && response.data.output_file) {
                // Set segNrrd to the output_file (nifti_path) for next step
                currentParams.segNrrd = response.data.output_file;
                // Set model_name to 'headandneck' for convertToDicomSeg
                currentParams.model_name = 'headandneck';
                continue;
              } else {
                throw new Error("No output_file returned from segment");
              }
            } catch (err) {
                                console.error("Error in segment step for Contour-HaN:", err);
              this.updateTask(patientId, { status: "Failed at segment" });
              throw err;
            }
          }
          if (step === "convertToDicomSeg") {
            try {
              // Step 3: Call /convert-to-dicomseg
              const requestBody = this.modelConfig.buildRequestBody(step, currentParams);
              // Ensure model_name is 'headandneck'
              requestBody.model_name = 'headandneck';
              const response = await axios.post(
                this.baseEndpoint + this.endpoints.convertToDicomSeg,
                toUrlSearchParams(requestBody),
                { headers: { "Content-Type": "application/x-www-form-urlencoded" }, responseType: "arraybuffer" }
              );
              // Step 4: Receive DICOM-SEG file and save to Firebase Storage
              if (response.data) {
                // Step 5: Recursively search for all DICOM folders
                const storage = getStorage();
                const folderBasePath = currentParams.decryptedFolderPath;
                if (!folderBasePath) throw new Error("No folder path for Firebase storage");
                const baseRef = storageRef(storage, folderBasePath);
                const allItems = await listAllFiles(baseRef);
                // Group items by folder
                const folderMap = new Map();
                allItems.forEach((item) => {
                  if (item.name.toLowerCase().endsWith(".dcm")) {
                    const folderPath = item.fullPath.substring(0, item.fullPath.lastIndexOf("/"));
                    if (!folderMap.has(folderPath)) folderMap.set(folderPath, []);
                    folderMap.get(folderPath).push(item);
                  }
                });
                // Step 6: Save DICOM-SEG file to each folder as dicom-seg-{random}.dcm
                const randomDigit = Math.floor(Math.random() * 9) + 1;
                const segFilename = `dicom-seg-${randomDigit}.dcm`;
                const segBlob = new Blob([response.data], { type: "application/dicom" });
                const uploadPromises = [];
                folderMap.forEach((items, folderPath) => {
                  const segRef = storageRef(storage, `${folderPath}/${segFilename}`);
                  uploadPromises.push(uploadBytes(segRef, segBlob));
                });
                await Promise.all(uploadPromises);
                
                this.updateTask(patientId, { status: "Done" });
                
                // Mark AI result for Contour-HaN after successful completion
                if (this.markAIResult) {
                  await this.markAIResult(patientId, null);
                }
                
                // Handle postpaid logging for Contour-HaN
                if (this.costingType && this.costingType.toLowerCase() === "postpaid") {
                  // We need to call storePostpaidAIUsage from AssignDoctor
                  // This will be handled by the executeAIModel function after runModel completes
                  // Postpaid logging will be handled by executeAIModel
                }
                
                // Continue to final deduction logic instead of returning early
                break; // Break out of the step loop to continue to final deduction
              } else {
                throw new Error("No DICOM-SEG file returned from convert-to-dicomseg");
              }
            } catch (err) {
                                console.error("Error in convert-to-dicomseg step for Contour-HaN:", err);
              this.updateTask(patientId, { status: "Failed at convert-to-dicomseg" });
              throw err;
            }
          }
        }
        
      // Before building requestBody for each step, add debug log for LIVER_SEGMENTATION glb step
      if (this.modelId === "LIVER_SEGMENTATION" && step === "glb") {
        
      }

      let requestBody;
      if (this.modelId === "LIVER_SEGMENTATION" && step === "glb") {
        // Only send merged_nifti and task_id
        requestBody = {
          merged_nifti: currentParams.merged_nifti,
          task_id: currentParams.task_id,
        };
      } else {
        requestBody = this.modelConfig.buildRequestBody
          ? this.modelConfig.buildRequestBody(step, currentParams)
          : currentParams;
      }

      // Defensive: For LIVER_SEGMENTATION glb step, ensure merged_nifti is a string
      if (this.modelId === "LIVER_SEGMENTATION" && step === "glb") {
        if (typeof requestBody.merged_nifti !== "string") {
          throw new Error("[LIVER_SEGMENTATION][glb step] merged_nifti is not a string. currentParams:", currentParams);
        }
        
      }
      
      


      const endpointForStep = this.endpoints[step];
      if (!endpointForStep) {
        console.warn(`No endpoint found for step "${step}". Skipping step.`);
        continue;
      }
      const fullUrl =
        this.baseEndpoint + endpointForStep.replace(":taskId", "");

      try {
        let dataToSend = requestBody;
        let headers = { "Content-Type": "application/json" };
        let responseType = undefined;

        // Special handling for CONTOUR_ABDOMEN dcmtonrrd step - create ZIP from Firebase
        if (this.modelId === "CONTOUR_ABDOMEN" && step === "dcmtonrrd") {
          // Start timing for file download
          // const downloadStartTime = Date.now();
          
          // Create ZIP from Firebase Storage
          const folderPath = getFolderBasePath(currentParams.decryptedFolderPath);
          
          // Get patient ID for ZIP naming (without random suffix for storage)
          const reqBodyForZip = this.modelConfig.buildRequestBody(step, currentParams);
          // Remove random suffix (last 4 digits) from patient_id for Firebase storage
          const basePatientId = reqBodyForZip.patient_id ? reqBodyForZip.patient_id.replace(/\d{4}$/, '') : patientId;
          
          const dicomZipBlob = await getOrCreateDicomZip(folderPath, basePatientId);
          
          // Calculate and log download time
          // const downloadEndTime = Date.now();
          // const downloadDuration = (downloadEndTime - downloadStartTime) / 1000; // Convert to seconds
          // const minutes = Math.floor(downloadDuration / 60);
          // const seconds = (downloadDuration % 60).toFixed(1);
          
          
          // Create FormData with the ZIP file
          const formData = new FormData();
          const zipFilename = generateZipFilename(reqBodyForZip, true);
          formData.append("dicom_zip", dicomZipBlob, zipFilename);
          formData.append("model_name", requestBody.model_name);
          formData.append("task_id", requestBody.task_id);
          
          dataToSend = formData;
          headers = undefined; // Let axios set headers for FormData
        }
        // Special handling for CONTOUR_ABDOMEN convert step - expect binary response
        else if (this.modelId === "CONTOUR_ABDOMEN" && step === "convert") {
          // Use URL-encoded form data for convert step
          dataToSend = toUrlSearchParams(requestBody);
          headers = { "Content-Type": "application/x-www-form-urlencoded" };
          responseType = "arraybuffer"; // Expect binary DICOM-SEG file
        }
        // For LIVER_SEGMENTATION merge step, use URL-encoded form data
        else if (this.modelId === "LIVER_SEGMENTATION" && step === "merge") {
          dataToSend = toUrlSearchParams(requestBody);
          headers = { "Content-Type": "application/x-www-form-urlencoded" };
        }
        // For LIVER_SEGMENTATION glb step, use FormData
        else if (this.modelId === "LIVER_SEGMENTATION" && step === "glb") {
          const formData = new FormData();
          formData.append("merged_nifti", requestBody.merged_nifti);
          formData.append("task_id", requestBody.task_id);
          dataToSend = formData;
          headers = undefined; // Let axios set headers for FormData
          responseType = "arraybuffer";
        } else if (this.useUrlEncoded) {
          dataToSend = toUrlSearchParams(requestBody);
          headers = { "Content-Type": "application/x-www-form-urlencoded" };
        }


        const response = await axios.post(fullUrl, dataToSend, {
          ...(headers ? { headers } : {}),
          ...(responseType ? { responseType } : {}),
        });


        if (response.data) {
          // Special handling for CONTOUR_ABDOMEN convert step - save DICOM-SEG to Firebase
          if (this.modelId === "CONTOUR_ABDOMEN" && step === "convert") {
            // Start timing for upload
            // const uploadStartTime = Date.now();
            
            // Generate filename with single-digit random number (1-9)
            const randomDigit = Math.floor(Math.random() * 9) + 1;
            const segFilename = `dicom-seg-${randomDigit}.dcm`;
            
            // Create blob from binary response
            const segBlob = new Blob([response.data], { type: "application/dicom" });
            
            // Get Firebase Storage reference
            const storage = getStorage();
            const firebasePath = currentParams.firebaseFolderPath || getFolderBasePath(currentParams.decryptedFolderPath);
            
            // Recursively find all folders containing DICOM files
            const baseRef = storageRef(storage, firebasePath);
            const allItems = await listAllFiles(baseRef);
            
            // Find unique folders that contain DICOM files
            const dicomFolders = new Set();
            allItems.forEach((item) => {
              if (item.name.toLowerCase().endsWith(".dcm")) {
                // Get the folder path (everything before the last '/')
                const folderPath = item.fullPath.substring(0, item.fullPath.lastIndexOf("/"));
                dicomFolders.add(folderPath);
              }
            });
            
            // Save DICOM-SEG to each folder containing DICOM files
            const uploadPromises = [];
            dicomFolders.forEach((folderPath) => {
              const segRef = storageRef(storage, `${folderPath}/${segFilename}`);
              uploadPromises.push(
                uploadBytes(segRef, segBlob)
              );
            });
            
            // Wait for all uploads to complete
            await Promise.all(uploadPromises);
            
            // Calculate and log upload time
            // const uploadEndTime = Date.now();
            // const uploadDuration = (uploadEndTime - uploadStartTime) / 1000; // Convert to seconds
            // const uploadMinutes = Math.floor(uploadDuration / 60);
            // const uploadSeconds = (uploadDuration % 60).toFixed(1);
            
            // Calculate total time for the entire CONTOUR_ABDOMEN process
            if (currentParams.downloadTime) {
              // const totalDuration = currentParams.downloadTime + uploadDuration;
            }
            
            // Store the filename in currentParams for reference
            currentParams.dicomSegFilename = segFilename;
            currentParams.dicomSegFolders = Array.from(dicomFolders);
            // currentParams.uploadTime = uploadDuration;
          }
          // Special handling for CONTOUR_ABDOMEN model's abdomen_heart_segmentation response
          else if (this.modelId === "CONTOUR_ABDOMEN" && step === "abdomen_heart_segmentation") {
            // Check for the nested structure
            if (response.data.run1 && response.data.run2) {
              // Extract the nested values and add them as top-level properties
              currentParams = {
                ...currentParams,
                ...response.data,
                heart_nrrd: response.data.run1.heart_nrrd,
                abdomen_nrrd: response.data.run2.abdomen_nrrd
              };
            } else {
              console.error(`[ERROR] Missing expected nested structure in abdomen_heart_segmentation response!`);
              
              // Try to salvage what we can from the response
              currentParams = { ...currentParams, ...response.data };
            }
          } else if (this.modelId === "LIVER_SEGMENTATION" && step === "merge") {
            // Keep merged_file (returned as merged_file from backend), output_dir and task_id for the next step
            currentParams = {
              merged_nifti: response.data.merged_file, // Backend returns 'merged_file', but glb expects 'merged_nifti'
              output_dir: response.data.output_dir,
              task_id: currentParams.task_id,
            };
          } else {
            // Standard handling for other steps
            currentParams = { ...currentParams, ...response.data };
          }
        }

        if (this.requiresPolling && currentParams.task_id) {
          await this.pollStatus(patientId, currentParams.task_id);
        }
      } catch (err) {
        console.error(`Error on step "${step}":`, err);
        this.updateTask(patientId, { status: "Failed" });
        throw err;
      }
    }

    // 4) If pipeline success => status: "Done"
    this.updateTask(patientId, { status: "Done" });
    if (this.toast) {
      if (this.modelId === "CXR_TB") {
        this.toast.success("CXR-TB Model Run Successfully", {
          position: "top-center",
        });
      } else if (this.modelId === "CXR_TB2") {
        this.toast.success("CXR-TB2 Model Run Successfully", {
          position: "top-center",
        });
      } else {
        this.toast.success(`${this.modelConfig.displayName} Done!`, {
          position: "top-center",
        });
      }
    }

    // 5) Mark AI result in DB FIRST
    if (this.markAIResult) {
      await this.markAIResult(patientId, null);
    }

    // 6) UNIFIED WALLET DEDUCTION: Handle ALL models for prepaid users
    if (this.handleWalletDeduction && this.costingType && this.costingType.toLowerCase() === "prepaid") {
      try {        
        await this.handleWalletDeduction(
          this.modelConfig.displayName || this.modelId, 
          patient, 
          this.triggeredByUser
        );
        
       
      } catch (err) {
        console.error("[AIModelHandler] Wallet deduction failed:", err);
        throw err;
      }
    }
    return currentParams;
  }

  /**
   * Embeds prediction metadata into image file
   * @param {File|Blob} imageFile - The image file to embed metadata into
   * @param {Object} metadata - The prediction metadata to embed
   * @param {string} format - Image format ('png' or 'jpg')
   * @returns {Promise<Blob>} - Image with embedded metadata
   */
  async embedMetadataInImage(imageFile, metadata, format) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Set canvas dimensions to match image with high DPI support
        const scale = window.devicePixelRatio || 1;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.style.width = img.width + 'px';
        canvas.style.height = img.height + 'px';
        
        // Scale context for high DPI displays
        ctx.scale(scale, scale);
        
        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw the original image
        ctx.drawImage(img, 0, 0, img.width, img.height);
        
        // Format prediction results like in 1.jsx
        const formatProbKey = (k) => {
          const lower = String(k).toLowerCase();
          if (lower === "tb") return "TB";
          if (lower === "healthy") return "Healthy";
          if (lower === "sick") return "Sick";
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        };
        
        const formatProbValue = (val) => {
          if (typeof val !== 'number') return String(val);
          return val.toFixed(4); // Show 4 decimal places
        };
        
        // Prepare two-column layout data
        const leftColumn = [
          `Confidence: ${formatProbValue(metadata.confidence)}`,
          `Predicted Class: ${metadata.predicted_class}`
        ];
        
        const rightColumn = ['Probabilities:'];
        
        // Add probabilities if available
        if (metadata.probabilities && typeof metadata.probabilities === 'object') {
          Object.entries(metadata.probabilities).forEach(([key, value]) => {
            rightColumn.push(`${formatProbKey(key)}: ${formatProbValue(value)}`);
          });
        }
        
        // Calculate overlay dimensions for two-column layout
        const lineHeight = 18;
        const padding = 15;
        const leftColumnWidth = 140;
        const rightColumnWidth = 120;
        const columnGap = 15;
        const overlayWidth = leftColumnWidth + rightColumnWidth + columnGap + (padding * 2);
        const maxRows = Math.max(leftColumn.length, rightColumn.length);
        const overlayHeight = maxRows * lineHeight + (padding * 2);
        
        // Position overlay so bottom edge aligns with bottom of image
        const overlayX = 15;
        const overlayY = img.height - overlayHeight - 5;
        
        // Draw semi-transparent background at bottom-left
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.fillRect(overlayX, overlayY, overlayWidth, overlayHeight);
        
        // Add border for better visibility
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        ctx.strokeRect(overlayX, overlayY, overlayWidth, overlayHeight);
        
        // Enable text anti-aliasing for crisp text
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';
        
        // Draw left column (Confidence and Predicted Class)
        leftColumn.forEach((text, index) => {
          const xPosition = overlayX + padding;
          const yPosition = overlayY + padding + (index * lineHeight);
          
          ctx.fillStyle = '#FFFFFF';
          ctx.font = '13px Arial, sans-serif';
          ctx.fillText(text, Math.round(xPosition), Math.round(yPosition));
        });
        
        // Draw right column (Probabilities)
        rightColumn.forEach((text, index) => {
          const xPosition = overlayX + padding + leftColumnWidth + columnGap;
          const yPosition = overlayY + padding + (index * lineHeight);
          
          // Set font and color based on text type
          if (text === 'Probabilities:') {
            ctx.fillStyle = '#FFD700'; // Gold color for section header
            ctx.font = 'bold 13px Arial, sans-serif';
          } else {
            ctx.fillStyle = '#E8E8E8'; // Light gray for probability values
            ctx.font = '13px Arial, sans-serif';
          }
          
          ctx.fillText(text, Math.round(xPosition), Math.round(yPosition));
        });
        
        // Convert canvas to blob with embedded metadata
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a new blob with custom properties for metadata
            const metadataBlob = new Blob([blob], { type: blob.type });
            
            // Store metadata as a custom property (for reference)
            metadataBlob.predictionMetadata = metadata;
            
            // Image saved with embedded prediction overlay
            
            resolve(metadataBlob);
          } else {
            reject(new Error('Failed to create image blob with metadata'));
          }
        }, format === 'png' ? 'image/png' : 'image/jpeg', 0.9);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for metadata embedding'));
      };
      
      // Load the image
      if (imageFile instanceof File || imageFile instanceof Blob) {
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target.result;
        };
        reader.readAsDataURL(imageFile);
      } else {
        reject(new Error('Invalid image file format'));
      }
    });
  }

  // Frontend color coding removed - backend now returns fully colored image with grouped boxes

  /**
   * Save AI_Report data to the latest scan in Firebase Realtime Database
   * @param {string} patientId - Patient ID
   * @param {Object} reportData - Report data containing report, bounding_boxes, debug_info, raw_result
   */
  async saveAIReportToLatestScan(patientId, reportData) {
    try {
      console.log("AIModelHandler - Saving AI_Report to latest scan for patient:", patientId);

      // Parse grouped_boxes into the required structure
      let parsedGroupedBoxes = null;
      if (reportData.grouped_boxes && Array.isArray(reportData.grouped_boxes)) {
        parsedGroupedBoxes = {};
        reportData.grouped_boxes.forEach((item, index) => {
          if (Array.isArray(item) && item.length >= 3) {
            const [explanation, , color] = item; // Skip boxes element (unused)
            if (color !== null && color !== undefined) {
              // Item has color - save Explanation and Color
              parsedGroupedBoxes[index] = {
                Explanation: explanation,
                Color: String(color)
              };
            } else {
              // Item has no color - save only Explanation
              parsedGroupedBoxes[index] = {
                Explanation: explanation
              };
            }
          }
        });
        console.log("AIModelHandler - Parsed grouped_boxes:", parsedGroupedBoxes);
      }

      // Determine classification based on annotated_image_base64
      // If annotated image exists → ABNORMAL, otherwise → NORMAL
      const classification = reportData.annotated_image_base64 && reportData.annotated_image_base64.trim() !== '' 
        ? 'ABNORMAL' 
        : 'NORMAL';
      
      console.log("AIModelHandler - Classification determined:", classification, "based on annotated_image_base64:", !!reportData.annotated_image_base64);

      // Prepare the report data - Save ONLY classification and grouped_boxes
      const reportDataToSave = {
        classification: classification, // ABNORMAL if annotated_image_base64 exists, otherwise NORMAL
        ...(parsedGroupedBoxes && { grouped_boxes: parsedGroupedBoxes }), // Only include if exists
      };

      console.log("AIModelHandler - Report data to save:", reportDataToSave);

      // Use patientId directly (no UUID lookup needed for non-UUID system)
      console.log("AIModelHandler - Using patientId directly:", patientId);

      // Get patient data to find the latest scan
      const patientRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}`);
      const snapshot = await get(patientRef);
      const patientData = snapshot.val();

      if (!patientData) {
        console.warn(`AIModelHandler - Patient data not found for ID: ${patientId}, saving AI_Report anyway`);

        // Save AI_Report even if patient data not found
        const aiReportRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}/AI_Report`);
        await set(aiReportRef, reportDataToSave);
        console.log("AIModelHandler - AI_Report saved successfully");
        return;
      }

      console.log("AIModelHandler - Patient data:", patientData);

      // Check if patient has Version 03 with uploads structure
      if (patientData.Version === "03" && patientData.uploads) {
        console.log("AIModelHandler - Processing Version 03 patient with uploads");
        
        // Find the latest scan
        const uploads = patientData.uploads;
        const scanKeys = Object.keys(uploads)
          .filter((key) => key.toLowerCase().includes("scan"))
          .map((key) => ({
            key,
            number: parseInt(key.replace(/\D/g, "")) || 0,
          }))
          .sort((a, b) => b.number - a.number);

        console.log("AIModelHandler - Found scan keys:", scanKeys);

        if (scanKeys.length > 0) {
          const latestScanKey = scanKeys[0].key;
          console.log("AIModelHandler - Latest scan key:", latestScanKey);
          
          // Save AI_Report to the latest scan
          const aiReportRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}/uploads/${latestScanKey}/AI_Report`);
          await set(aiReportRef, reportDataToSave);
          
          console.log("AIModelHandler - AI_Report saved to latest scan successfully");
        } else {
          console.warn("AIModelHandler - No scans found in uploads, saving to patient level");
          
          // Fallback to patient level if no scans found
          const aiReportRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}/AI_Report`);
          await set(aiReportRef, reportDataToSave);
        }
      } else {
        console.log("AIModelHandler - Processing legacy patient, saving to patient level");
        
        // For legacy patients, save directly to patient level
        const aiReportRef = ref(db, `superadmin/admins/${this.adminName}/patients/${patientId}/AI_Report`);
        await set(aiReportRef, reportDataToSave);
      }
      
      console.log("AIModelHandler - AI_Report saved successfully");
      
    } catch (error) {
      console.error("AIModelHandler - Error saving AI_Report:", error);
      throw error;
    }
  }

  /**
   * Get AI_Report data from Firebase Realtime Database for a patient
   * This method handles both Version 03 patients (with uploads structure) and legacy patients
   * For Version 03: First checks latest scan, then falls back to patient level if not found
   * @param {string} patientId - Patient ID
   * @param {string} adminName - Admin name
   * @returns {Object|null} AI_Report data or null if not found
   */
  static async getAIReport(patientId, adminName) {
    try {
      console.log("AIModelHandler - Getting AI_Report for patient:", patientId);
      
      // Get patient data to determine structure
      const patientDbRef = ref(db, `superadmin/admins/${adminName}/patients/${patientId}`);
      const patientSnapshot = await get(patientDbRef);
      const patientData = patientSnapshot.val();

      if (!patientData) {
        console.warn(`AIModelHandler - Patient data not found for ID: ${patientId}`);
        return null;
      }

      // Check if patient has Version 03 with uploads structure
      if (patientData.Version === "03" && patientData.uploads) {
        console.log("AIModelHandler - Getting AI_Report for Version 03 patient with uploads");
        
        // Find the latest scan
        const uploads = patientData.uploads;
        const scanKeys = Object.keys(uploads)
          .filter((key) => key.toLowerCase().includes("scan"))
          .map((key) => ({
            key,
            number: parseInt(key.replace(/\D/g, "")) || 0,
          }))
          .sort((a, b) => b.number - a.number);

        console.log("AIModelHandler - Found scan keys for retrieval:", scanKeys);

        if (scanKeys.length > 0) {
          const latestScanKey = scanKeys[0].key;
          console.log("AIModelHandler - Latest scan key:", latestScanKey);
          
          // Try to get AI_Report from the latest scan
          const scanAiReportRef = ref(
            db,
            `superadmin/admins/${adminName}/patients/${patientId}/uploads/${latestScanKey}/AI_Report`
          );
          const scanSnapshot = await get(scanAiReportRef);
          
          if (scanSnapshot.exists()) {
            console.log("AIModelHandler - Found AI_Report in latest scan");
            return scanSnapshot.val();
          } else {
            console.log("AIModelHandler - AI_Report not found in latest scan, checking patient level");
          }
        }
        
        // Fallback: Check patient level for Version 03 patients
        const patientAiReportRef = ref(
          db,
          `superadmin/admins/${adminName}/patients/${patientId}/AI_Report`
        );
        const fallbackSnapshot = await get(patientAiReportRef);
        
        if (fallbackSnapshot.exists()) {
          console.log("AIModelHandler - Found AI_Report at patient level (fallback)");
          return fallbackSnapshot.val();
        }
      } else {
        console.log("AIModelHandler - Getting AI_Report for legacy patient");
        
        // For legacy patients, get AI_Report from patient level
        const aiReportRef = ref(
          db,
          `superadmin/admins/${adminName}/patients/${patientId}/AI_Report`
        );
        const snapshot = await get(aiReportRef);
        
        if (snapshot.exists()) {
          console.log("AIModelHandler - Found AI_Report at patient level");
          return snapshot.val();
        }
      }
      
      console.log("AIModelHandler - No AI_Report found");
      return null;
      
    } catch (error) {
      console.error("AIModelHandler - Error getting AI_Report:", error);
      return null;
    }
  }

  static async resetAIReport(patientId, adminName) {
    try {
      console.log("AIModelHandler - Resetting AI_Report for patient:", patientId);
      
      // Get patient data to determine structure
      const patientDbRef = ref(db, `superadmin/admins/${adminName}/patients/${patientId}`);
      const patientSnapshot = await get(patientDbRef);
      const patientData = patientSnapshot.val();

      if (!patientData) {
        console.warn(`AIModelHandler - Patient data not found for ID: ${patientId}`);
        return;
      }

      // Check if patient has Version 03 with uploads structure
      if (patientData.Version === "03" && patientData.uploads) {
        console.log("AIModelHandler - Resetting AI_Report for Version 03 patient with uploads");
        
        // For Version 03 patients, delete AI_Report from all scans
        const uploads = patientData.uploads;
        const scanKeys = Object.keys(uploads).filter((key) =>
          key.toLowerCase().includes("scan")
        );

        console.log("AIModelHandler - Found scan keys for reset:", scanKeys);

        for (const scanKey of scanKeys) {
          const aiReportRef = ref(
            db,
            `superadmin/admins/${adminName}/patients/${patientId}/uploads/${scanKey}/AI_Report`
          );
          await set(aiReportRef, null); // Delete the AI_Report node
          console.log(`AIModelHandler - Deleted AI_Report from scan: ${scanKey}`);
        }
        
        // Also delete from patient level (fallback location)
        const patientAiReportRef = ref(
          db,
          `superadmin/admins/${adminName}/patients/${patientId}/AI_Report`
        );
        await set(patientAiReportRef, null);
        console.log("AIModelHandler - Deleted AI_Report from patient level (fallback)");
      } else {
        console.log("AIModelHandler - Resetting AI_Report for legacy patient");
        
        // For legacy patients, delete AI_Report from patient level
        const aiReportRef = ref(
          db,
          `superadmin/admins/${adminName}/patients/${patientId}/AI_Report`
        );
        await set(aiReportRef, null); // Delete the AI_Report node
        console.log("AIModelHandler - Deleted AI_Report from patient level");
      }
      
      console.log("AIModelHandler - AI_Report reset completed successfully");
      
    } catch (error) {
      console.error("AIModelHandler - Error resetting AI_Report:", error);
      throw error;
    }
  }

}